/**
 * Core hook for plugin builder session management.
 * Handles session CRUD, streaming, and guided flow state.
 */

import { useState, useCallback, useRef } from 'react';
import { useStreaming } from '@/lib/hooks/useStreaming';
import { authApi } from '@/lib/auth-api';
import api from '@/lib/api';
import type {
  PluginBuilderSession,
  VirtualFileSystem,
  GuidedPhase,
  GuidedFlow,
  BuildPlan,
  BuilderMode,
} from '../types';

const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';

export interface UsePluginBuilderSessionReturn {
  // Session state
  session: PluginBuilderSession | null;
  isLoading: boolean;
  error: string | null;

  // Files
  files: VirtualFileSystem | null;

  // Conversation
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  streamingText: string;
  isSending: boolean;

  // Guided flow
  guidedFlow: GuidedFlow | null;
  buildPlan: BuildPlan | null;
  builderMode: BuilderMode;

  // Actions
  loadSession: (sessionId: string) => Promise<void>;
  sendMessage: (message: string, guidedPhase?: GuidedPhase) => Promise<void>;
  startGuidedFlow: (idea: string) => Promise<void>;
  setBuilderMode: (mode: BuilderMode) => void;
  saveSession: (name: string, description?: string) => Promise<void>;
  resetContext: () => Promise<void>;

  // Preview
  buildPreview: () => Promise<string | null>;
  previewHtml: string | null;
  isBuildingPreview: boolean;
  buildError: string | null;
}

export function usePluginBuilderSession(): UsePluginBuilderSessionReturn {
  const authToken = authApi.getIdToken();
  const [session, setSession] = useState<PluginBuilderSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<VirtualFileSystem | null>(null);
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>(
    [],
  );
  const [isSending, setIsSending] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [guidedFlow, setGuidedFlow] = useState<GuidedFlow | null>(null);
  const [buildPlan, setBuildPlan] = useState<BuildPlan | null>(null);
  const [builderMode, setBuilderMode] = useState<BuilderMode>('guided');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isBuildingPreview, setIsBuildingPreview] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);

  // Track accumulated streaming text per node
  const streamingTextRef = useRef<string>('');

  // Streaming hook for WebSocket connection
  const streaming = useStreaming({
    requestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    onTextUpdate: useCallback((_nodeId: string, text: string) => {
      streamingTextRef.current += text;
    }, []),
    onSettingsUpdate: useCallback((settings: Record<string, unknown>) => {
      // Update guided flow and build plan from settings broadcasts
      if (settings.guidedFlow) {
        setGuidedFlow(settings.guidedFlow as GuidedFlow);
      }
      if (settings.buildPlan) {
        setBuildPlan(settings.buildPlan as BuildPlan);
      }
      // Update files from settings
      if (settings.currentFiles) {
        setFiles(settings.currentFiles as VirtualFileSystem);
      }
    }, []),
    onComplete: useCallback(
      (_completedRequestId: string) => {
        setIsSending(false);

        // Add assistant message from accumulated text
        const assistantText = streamingTextRef.current.trim();
        if (assistantText) {
          setMessages((prev) => [...prev, { role: 'assistant', content: assistantText }]);
        }
        streamingTextRef.current = '';

        // Re-fetch session to get latest state
        if (session?.sessionId) {
          api.getPluginBuilderSession(session.sessionId).then((result) => {
            const sess = result.session as PluginBuilderSession;
            setSession(sess);
            const settings = sess.currentSettings as Record<string, unknown>;
            if (settings?.currentFiles) {
              setFiles(settings.currentFiles as VirtualFileSystem);
            }
            if (settings?.guidedFlow) {
              setGuidedFlow(settings.guidedFlow as GuidedFlow);
            }
            if (settings?.buildPlan) {
              setBuildPlan(settings.buildPlan as BuildPlan);
            }
          });
        }
      },
      [session?.sessionId],
    ),
    onError: useCallback((_code: string, message: string) => {
      setError(message);
      setIsSending(false);
    }, []),
  });

  // Load a session by ID
  const loadSession = useCallback(async (sessionId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.getPluginBuilderSession(sessionId);
      const sess = result.session as PluginBuilderSession;
      setSession(sess);
      setMessages(
        sess.conversationHistory.map((m) => ({
          role: m.role,
          content: m.content,
        })),
      );

      const settings = sess.currentSettings as Record<string, unknown>;
      if (settings?.currentFiles) {
        setFiles(settings.currentFiles as VirtualFileSystem);
      }
      if (settings?.guidedFlow) {
        setGuidedFlow(settings.guidedFlow as GuidedFlow);
      }
      if (settings?.buildPlan) {
        setBuildPlan(settings.buildPlan as BuildPlan);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send a message to Claude
  const sendMessage = useCallback(
    async (message: string, guidedPhase?: GuidedPhase) => {
      if (!session?.sessionId || isSending) return;

      setIsSending(true);
      setError(null);
      streamingTextRef.current = '';

      // Add user message to local state immediately
      setMessages((prev) => [...prev, { role: 'user', content: message }]);

      try {
        const result = await api.startPluginBuilderMessageStream(
          session.sessionId,
          message,
          false, // verbose
          false, // useOpus
          guidedPhase,
        );
        setRequestId(result.requestId);
      } catch (err) {
        setError((err as Error).message);
        setIsSending(false);
      }
    },
    [session?.sessionId, isSending],
  );

  // Start guided flow
  const startGuidedFlow = useCallback(
    async (idea: string) => {
      if (!session?.sessionId) return;

      try {
        const result = await api.startPluginGuidedFlow(session.sessionId, idea);
        setGuidedFlow(result.guidedFlow as GuidedFlow);
        setBuilderMode('guided');
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [session?.sessionId],
  );

  // Save session
  const saveSession = useCallback(
    async (name: string, description?: string) => {
      if (!session?.sessionId) return;

      try {
        await api.savePluginBuilderSession(session.sessionId, { name, description });
        setSession((prev) => (prev ? { ...prev, currentName: name } : null));
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [session?.sessionId],
  );

  // Reset context
  const resetContext = useCallback(async () => {
    if (!session?.sessionId) return;

    try {
      await api.resetPluginBuilderContext(session.sessionId);
      setMessages([]);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [session?.sessionId]);

  // Build preview
  const buildPreview = useCallback(async (): Promise<string | null> => {
    if (!session?.sessionId) return null;

    setIsBuildingPreview(true);
    setBuildError(null);

    try {
      const result = await api.buildPluginPreview(session.sessionId);
      if (result.success) {
        setPreviewHtml(result.html);
        return result.html;
      }
      return null;
    } catch (err) {
      const message = (err as Error).message;
      setBuildError(message);
      return null;
    } finally {
      setIsBuildingPreview(false);
    }
  }, [session?.sessionId]);

  // Current streaming text (from the streaming hook's accumulated text per node)
  const currentStreamingText = Object.values(streaming.streamingText).join('');

  return {
    session,
    isLoading,
    error,
    files,
    messages,
    streamingText: currentStreamingText,
    isSending,
    guidedFlow,
    buildPlan,
    builderMode,
    loadSession,
    sendMessage,
    startGuidedFlow,
    setBuilderMode,
    saveSession,
    resetContext,
    buildPreview,
    previewHtml,
    isBuildingPreview,
    buildError,
  };
}
